import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { fetchOrderStats } from '../../data/accounts.ts';
import { toAppError } from '../../data/errors.ts';
import { listTechnicians, searchOrders, type SearchParams } from '../../data/orders.ts';
import { isStaff } from '../../domain/roles.ts';
import { ORDER_STATUSES, STATUS_LABELS, type OrderStatus } from '../../domain/status.ts';
import { Button } from '../../ui/Button.tsx';
import { useDebounced } from '../../ui/use-debounced.ts';
import { ListSelect } from '../../ui/ListSelect.tsx';
import { MarqueeText } from '../../ui/MarqueeText.tsx';
import { uppercaseChange } from '../../ui/uppercase.ts';
import {
  ClockIcon,
  PackageSearchIcon,
  PackageCheckIcon,
  CheckCircleIcon,
  PlusIcon,
  SearchIcon,
} from '../../ui/icons.tsx';
import { useProfile } from '../auth/auth-context.ts';
import { OrderList, OrderViewToggle } from './OrderList.tsx';
import { useOrderView } from './useOrderView.ts';

type Filter = { kind: 'all' } | { kind: 'open' } | { kind: 'status'; status: OrderStatus } | { kind: 'unassigned' };

/**
 * Al entrar al panel y al limpiar la búsqueda se ven las órdenes pendientes (incluye Listo: el personal las ve
 * primero para revisarlas; el técnico, al final hasta que se cierren o se le devuelvan objetadas).
 */
const DEFAULT_FILTER: Filter = { kind: 'open' };

export function OrdersPage() {
  const profile = useProfile();
  const staff = isStaff(profile.role);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>(DEFAULT_FILTER);
  const [technician, setTechnician] = useState('');
  const [view, setView] = useOrderView();
  const debouncedQuery = useDebounced(query, 300);

  const params: SearchParams = {
    query: debouncedQuery,
    status: filter.kind === 'status' ? filter.status : null,
    unassigned: filter.kind === 'unassigned',
    pending: filter.kind === 'open',
    technician: staff && technician !== '' ? technician : null,
  };

  const stats = useQuery({ queryKey: ['order-stats'], queryFn: fetchOrderStats });
  const technicians = useQuery({ queryKey: ['technicians'], queryFn: listTechnicians, enabled: staff });
  const orders = useInfiniteQuery({
    queryKey: ['orders', params],
    queryFn: ({ pageParam }) => searchOrders(params, pageParam),
    initialPageParam: null as Awaited<ReturnType<typeof searchOrders>>['next_cursor'],
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const hasFilters = query !== '' || filter.kind !== DEFAULT_FILTER.kind || technician !== '';
  const clearFilters = () => {
    setQuery('');
    setFilter(DEFAULT_FILTER);
    setTechnician('');
  };

  const items = orders.data?.pages.flatMap((p) => p.items) ?? [];
  const total = orders.data?.pages[0]?.total ?? 0;

  return (
    <div className={view === 'table' ? 'page page-wide' : 'page'}>
      <div className="page-header">
        <div>
          <span className="page-eyebrow">Hola, {profile.name.trim().split(/\s+/)[0]}</span>
          <h1>Panel de control</h1>
        </div>
        {staff ? (
          <Link to="/new" className="btn btn-primary">
            <PlusIcon />
            Nueva orden
          </Link>
        ) : null}
      </div>

      {stats.data ? (
        <ul className="stats" aria-label="Resumen de órdenes">
          <Stat
            label="Órdenes pendientes"
            value={stats.data.pending}
            tone="primary"
            icon={<ClockIcon />}
            selected={filter.kind === 'open'}
            onSelect={() => setFilter({ kind: 'open' })}
          />
          <Stat
            label="Esperando repuestos"
            value={stats.data.chequeo_repuestos}
            tone="warning"
            icon={<PackageSearchIcon />}
            selected={filter.kind === 'status' && filter.status === 'chequeo_repuestos'}
            onSelect={() => setFilter({ kind: 'status', status: 'chequeo_repuestos' })}
          />
          <Stat
            label="Repuestos disponibles"
            value={stats.data.repuesto_despachado}
            tone="info"
            icon={<PackageCheckIcon />}
            selected={filter.kind === 'status' && filter.status === 'repuesto_despachado'}
            onSelect={() => setFilter({ kind: 'status', status: 'repuesto_despachado' })}
          />
          <Stat
            label="Trabajos realizados"
            value={stats.data.cerrada}
            tone="ok"
            icon={<CheckCircleIcon />}
            selected={filter.kind === 'status' && filter.status === 'cerrada'}
            onSelect={() => setFilter({ kind: 'status', status: 'cerrada' })}
          />
        </ul>
      ) : null}

      <div className="search-bar">
        <div className="field">
          <label htmlFor="order-search">Buscar</label>
          <span className="search-input">
            <SearchIcon />
            <input
              id="order-search"
              type="search"
              placeholder="OS, cliente, equipo o marca"
              autoCapitalize="characters"
              value={query}
              onChange={uppercaseChange((e) => setQuery(e.target.value))}
              maxLength={100}
            />
          </span>
        </div>
        <ListSelect
          label="Estado"
          value={filter.kind === 'status' ? filter.status : filter.kind}
          onChange={(value) => setFilter(filterFromValue(value))}
          options={[
            { value: 'all', label: 'Todos los estados' },
            { value: 'open', label: 'Órdenes pendientes' },
            ...(staff ? [{ value: 'unassigned', label: 'Sin asignar', labelClassName: 'unassigned' }] : []),
            ...ORDER_STATUSES.map((status) => ({
              value: status,
              label: STATUS_LABELS[status],
              tone: `status-${status}`,
            })),
          ]}
        />
        {staff ? (
          <ListSelect
            label="Técnico"
            value={technician}
            onChange={setTechnician}
            options={[
              { value: '', label: 'Todos los técnicos' },
              ...(technicians.data ?? []).map((t) => ({
                value: t.id,
                label: `${t.name}${t.active ? '' : ' (inactivo)'}`,
              })),
            ]}
          />
        ) : null}
      </div>

      <div className="results-bar">
        <p className="muted" aria-live="polite">
          {orders.isSuccess ? `${total} ${total === 1 ? 'orden' : 'órdenes'}` : ' '}
        </p>
        <div className="results-actions">
          {hasFilters ? (
            <Button variant="ghost" className="btn-restore" onClick={clearFilters}>
              Limpiar búsqueda
            </Button>
          ) : null}
          <OrderViewToggle view={view} onChange={setView} />
        </div>
      </div>

      {orders.isPending ? <p aria-busy="true">Cargando órdenes…</p> : null}
      {orders.isError ? (
        <div className="card">
          <p>{toAppError(orders.error).message}</p>
          <Button variant="secondary" onClick={() => void orders.refetch()}>
            Reintentar
          </Button>
        </div>
      ) : null}
      {orders.isSuccess && items.length === 0 ? (
        <div className="card empty">
          <p>
            {debouncedQuery || technician || filter.kind !== DEFAULT_FILTER.kind
              ? 'Ninguna orden coincide con la búsqueda.'
              : 'No hay órdenes pendientes.'}
          </p>
        </div>
      ) : null}

      <OrderList orders={items} view={view} staff={staff} />

      {orders.hasNextPage ? (
        <Button variant="secondary" block busy={orders.isFetchingNextPage} onClick={() => void orders.fetchNextPage()}>
          Cargar más
        </Button>
      ) : null}
    </div>
  );
}

function filterFromValue(value: string): Filter {
  if (value === 'unassigned') return { kind: 'unassigned' };
  if (value === 'all') return { kind: 'all' };
  if (value === 'open') return { kind: 'open' };
  return { kind: 'status', status: value as OrderStatus };
}

interface StatProps {
  label: string;
  value: number;
  tone: 'primary' | 'info' | 'warning' | 'ok';
  icon: ReactNode;
  selected: boolean;
  onSelect: () => void;
}

// Tarjeta de resumen: al tocarla aplica el filtro de ese estado (la de pendientes vuelve al filtro inicial).
function Stat({ label, value, tone, icon, selected, onSelect }: StatProps) {
  return (
    <li>
      <button type="button" className={`stat stat-${tone}`} aria-pressed={selected} onClick={onSelect}>
        <span className="stat-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="stat-value">{value ?? 0}</span>
        <MarqueeText className="stat-label">{label}</MarqueeText>
      </button>
    </li>
  );
}
